import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../client';
import { useDraftMutation } from '../helpers/draftMutation';
import {
  applyDraftCache,
  optimisticDraftUpdate,
  restoreDraftCache
} from '../helpers/draftCacheHelpers';
import { expectSingleNode, reconcileEnvelope, transformDraftNode } from '../../draftGraph';
import { processLayoutKeys } from '../../query-keys';
import {
  type AddDraftEventAssignmentsRequest,
  type AddDraftEventRequest,
  type AttachDraftEventRequest,
  type DraftNodeEnvelope,
  type GetProcessDraftResponse,
  type GetProcessLayoutResponse,
  type RemoveDraftEventAssignmentsRequest,
  type UpdateDraftEventRequest
} from '@bliks-io/contracts';
import { basePath } from './shared';

type DraftCacheNode =
  | GetProcessDraftResponse['steps'][number]
  | GetProcessDraftResponse['draftAssignments'][number];

function hasEventInput(node: DraftCacheNode, eventId: string): boolean {
  return (node.inputs ?? []).some(
    (input) => input.inputType === 'event' && input.eventId && String(input.eventId) === eventId
  );
}

/**
 * Optimistic mirror of the backend attach: append an `event` input linking
 * `eventId` to the target node (step OR assignment — event↔suggestion edges
 * are first-class). Snapshot fields are denormalized from the cached event
 * row when present; the envelope reconcile replaces them with server truth.
 */
function attachEventInputToTarget(
  draft: GetProcessDraftResponse,
  targetStepId: string,
  eventId: string
): GetProcessDraftResponse {
  const eventRow = (draft.events ?? []).find((e) => String(e.stableStepId) === eventId);
  return transformDraftNode(draft, targetStepId, (entry) => {
    if (entry.kind === 'event') return entry.node;
    if (hasEventInput(entry.node, eventId)) return entry.node;
    const eventInput = {
      inputType: 'event' as const,
      eventId,
      type: eventRow?.eventType ?? 'none',
      name: eventRow?.name ?? '',
      description: eventRow?.description ?? ''
    };
    return {
      ...entry.node,
      inputs: [...(entry.node.inputs ?? []), eventInput]
    } as typeof entry.node;
  });
}

/**
 * Optimistic mirror of the backend detach (the inverse of attach, matching
 * `stripStartSource` semantics for start events): drop the eventId-linked
 * event input, and — when detaching a start event — also strip the legacy
 * START linkage forms (sourceSteps tags, `previousStep`) so legacy-shaped
 * first steps render detached immediately too.
 */
function stripEventAttachmentFromTarget(
  draft: GetProcessDraftResponse,
  targetStepId: string,
  eventId: string
): GetProcessDraftResponse {
  const isStartEvent = (draft.events ?? []).some(
    (e) => String(e.stableStepId) === eventId && e.eventKind === 'start'
  );
  const isStartRef = (stepId: unknown): boolean =>
    String(stepId) === 'START' || String(stepId) === eventId;

  return transformDraftNode(draft, targetStepId, (entry) => {
    if (entry.kind === 'event') return entry.node;
    const node = entry.node;
    const inputs = node.inputs ?? [];
    let changed = false;
    const nextInputs: typeof inputs = [];
    for (const input of inputs) {
      if (input.inputType === 'event' && input.eventId && String(input.eventId) === eventId) {
        changed = true;
        continue;
      }
      if (!isStartEvent) {
        nextInputs.push(input);
        continue;
      }
      // Legacy START tags live in an out-of-schema `sourceSteps` field on
      // event inputs and in regular previous_output sources.
      const sources = (input as { sourceSteps?: Array<{ stepId: unknown }> }).sourceSteps;
      if (!sources?.some((s) => isStartRef(s.stepId))) {
        nextInputs.push(input);
        continue;
      }
      changed = true;
      if (input.inputType === 'event') {
        const { sourceSteps: _omit, ...rest } = input as typeof input & {
          sourceSteps?: unknown;
        };
        nextInputs.push(rest as typeof input);
        continue;
      }
      const filtered = sources.filter((s) => !isStartRef(s.stepId));
      if (filtered.length === 0) continue;
      nextInputs.push({ ...(input as object), sourceSteps: filtered } as (typeof inputs)[number]);
    }

    const prev = (node as { previousStep?: unknown }).previousStep;
    const clearPreviousStep =
      entry.kind === 'step' &&
      prev != null &&
      (isStartEvent ? isStartRef(prev) : String(prev) === eventId);

    if (!changed && !clearPreviousStep) return node;
    return {
      ...node,
      inputs: nextInputs,
      ...(clearPreviousStep ? { previousStep: null } : {})
    } as typeof node;
  });
}

export function useAddDraftEvent(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async (request: AddDraftEventRequest): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(
        `${basePath(processId)}/events`,
        request
      );
      return data;
    },
    onSuccess: (envelope, { position }) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
      const created = expectSingleNode(envelope, 'event');
      const layoutKey = processLayoutKeys.detail(processId, 'draft');
      queryClient.setQueryData<GetProcessLayoutResponse>(layoutKey, (prev) => {
        if (!prev) return prev;
        // Node-id keyed (ADR 0003 P3b): event positions live in stepPositions
        // under the event's public id (= the row's stableStepId).
        return {
          ...prev,
          stepPositions: [
            ...prev.stepPositions,
            { stableStepId: String(created.stableStepId), x: position.x, y: position.y }
          ]
        };
      });
    }
  });
}

export function useUpdateDraftEvent(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      eventId,
      patch
    }: {
      eventId: string;
      patch: UpdateDraftEventRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.patch<DraftNodeEnvelope>(
        `${basePath(processId)}/events/${eventId}`,
        patch
      );
      return data;
    },
    onMutate: async ({ eventId, patch }) => {
      return optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        events: (old.events ?? []).map((e) =>
          String(e.stableStepId) === eventId ? { ...e, ...patch } : e
        )
      }));
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    // Name/type/description edits cascade into step input snapshots; the
    // envelope carries the full updated docs, so the cache converges exactly
    // without waiting for the settle backstop's refetch.
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

export function useDeleteDraftEvent(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async (eventId: string): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.delete<DraftNodeEnvelope>(
        `${basePath(processId)}/events/${eventId}`
      );
      return data;
    },
    onMutate: async (eventId) => {
      return optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        events: (old.events ?? []).filter((e) => String(e.stableStepId) !== eventId),
        steps: old.steps.map((s) => {
          const next = (s.inputs ?? []).filter(
            (input) =>
              input.inputType !== 'event' || !input.eventId || String(input.eventId) !== eventId
          );
          return next.length === s.inputs.length ? s : { ...s, inputs: next };
        })
      }));
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

export function useAttachDraftEvent(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      eventId,
      body
    }: {
      eventId: string;
      body: AttachDraftEventRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(
        `${basePath(processId)}/events/${eventId}/attach`,
        body
      );
      return data;
    },
    onMutate: async ({ eventId, body }) => {
      return optimisticDraftUpdate(queryClient, processId, (old) =>
        attachEventInputToTarget(old, body.targetStepId, eventId)
      );
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      // The envelope routes the target into whichever collection it lives in
      // (steps OR draftAssignments) — attach to a suggestion renders
      // immediately instead of waiting for the settle refetch.
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * Create draft assignments attached to the start event, one per newly-picked
 * assignee (already-attached assignees are skipped server-side). The envelope
 * is authoritative — write it into the cache so the UI converges before the
 * settle backstop's refetch lands.
 */
export function useAddDraftEventAssignments(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      eventId,
      body
    }: {
      eventId: string;
      body: AddDraftEventAssignmentsRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(
        `${basePath(processId)}/events/${eventId}/assignments`,
        body
      );
      return data;
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * The reverse of {@link useAddDraftEventAssignments}: the server detaches or
 * deletes every start-attached assignment carrying a listed assignee and
 * reports both in the envelope.
 */
export function useRemoveDraftEventAssignments(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      eventId,
      body
    }: {
      eventId: string;
      body: RemoveDraftEventAssignmentsRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.delete<DraftNodeEnvelope>(
        `${basePath(processId)}/events/${eventId}/assignments`,
        { data: body }
      );
      return data;
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

export function useDetachDraftEvent(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      eventId,
      stepId
    }: {
      eventId: string;
      stepId: string;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.delete<DraftNodeEnvelope>(
        `${basePath(processId)}/events/${eventId}/attach/${stepId}`
      );
      return data;
    },
    onMutate: async ({ eventId, stepId }) => {
      return optimisticDraftUpdate(queryClient, processId, (old) =>
        stripEventAttachmentFromTarget(old, stepId, eventId)
      );
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * Promote a legacy event input (no `eventId`) into a first-class event. Called
 * lazily by the graph editor on first interaction with a legacy event node —
 * the backend creates a `ProcessDraftEvent` seeded from the input's snapshot
 * and wires the input to it. The envelope (event row + rewired step) is
 * authoritative; the cache is patched directly so the follow-up action
 * (delete, detach, attach) runs against the canonical id without a refetch
 * round-trip.
 */
export function useUpgradeLegacyEventInput(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      stepId,
      inputIndex
    }: {
      stepId: string;
      inputIndex: number;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(
        `${basePath(processId)}/steps/${stepId}/inputs/${inputIndex}/upgrade-event`
      );
      return data;
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}
